const UTM_KEY = 'cb_utm_params';
const UTM_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface StoredUTM {
  utmSource: string;
  utmCampaign: string;
  utmMedium: string;
  /** C94: which ad / which placement. Optional because entries stored before C94 lack them. */
  utmContent?: string;
  utmTerm?: string;
  storedAt: string;
}

export interface UTMParams {
  utmSource: string;
  utmCampaign: string;
  utmMedium: string;
  utmContent?: string;
  utmTerm?: string;
  lid?: string;
}

/**
 * Read URL search params and store UTM values in localStorage.
 * New URL params always overwrite stored values (latest campaign wins).
 * Call once at app init (e.g. alongside initTracker).
 */
export function captureUTMFromURL(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    const campaign = params.get('utm_campaign');
    const medium = params.get('utm_medium');
    const content = params.get('utm_content');
    const term = params.get('utm_term');

    const lid = params.get('lid');

    if (source || campaign || medium || content || term || lid) {
      const stored: StoredUTM = {
        utmSource: source || '',
        utmCampaign: campaign || '',
        utmMedium: medium || '',
        utmContent: content || '',
        utmTerm: term || '',
        storedAt: new Date().toISOString(),
      };
      localStorage.setItem(UTM_KEY, JSON.stringify(stored));
      if (lid) localStorage.setItem('cb_lid', lid);
    }
  } catch {
    // localStorage unavailable (private browsing, etc.)
  }
}

/**
 * Return stored UTM params. Returns empty strings if expired or missing.
 */
export function getUTMParams(): UTMParams {
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (!raw) return { utmSource: '', utmCampaign: '', utmMedium: '', lid: localStorage.getItem('cb_lid') || '' };

    const stored: StoredUTM = JSON.parse(raw);
    const age = Date.now() - new Date(stored.storedAt).getTime();
    if (age > UTM_MAX_AGE_MS) {
      localStorage.removeItem(UTM_KEY);
      return { utmSource: '', utmCampaign: '', utmMedium: '', lid: localStorage.getItem('cb_lid') || '' };
    }

    return {
      utmSource: stored.utmSource || '',
      utmCampaign: stored.utmCampaign || '',
      utmMedium: stored.utmMedium || '',
      utmContent: stored.utmContent || '',
      utmTerm: stored.utmTerm || '',
      lid: localStorage.getItem('cb_lid') || '',
    };
  } catch {
    return { utmSource: '', utmCampaign: '', utmMedium: '' };
  }
}

/**
 * Return backend-compatible UTM fields for form payloads.
 * Matches the toLeadPayload pattern: utm_medium is piped into utm_source.
 */
/**
 * Build the advisor.colaberry.ai URL preserving UTM params from current page.
 */
export function getAdvisoryUrl(path: string = '/advisory/'): string {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
      .filter(k => params.get(k))
      .map(k => `${k}=${encodeURIComponent(params.get(k) || '')}`)
      .join('&');
    return `https://advisor.colaberry.ai${path}${utm ? '?' + utm : ''}`;
  } catch {
    return `https://advisor.colaberry.ai${path}`;
  }
}

/**
 * Build a demo walkthrough URL for a specific industry scenario.
 * Includes UTM passthrough + lid for identity resolution.
 */
export function getDemoWalkthroughUrl(scenario: string): string {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
      .filter(k => params.get(k))
      .map(k => `${k}=${encodeURIComponent(params.get(k) || '')}`)
      .join('&');
    const lid = localStorage.getItem('cb_lead_id') || localStorage.getItem('cb_lid') || '';
    const lidParam = lid ? `lid=${lid}` : '';
    const extra = [utm, lidParam].filter(Boolean).join('&');
    return `https://advisor.colaberry.ai/advisory/demo/walkthrough?scenario=${scenario}${extra ? '&' + extra : ''}`;
  } catch {
    return `https://advisor.colaberry.ai/advisory/demo/walkthrough?scenario=${scenario}`;
  }
}

export function getUTMPayloadFields(): {
  utm_source?: string;
  utm_campaign?: string;
  page_url: string;
} {
  const { utmSource, utmCampaign, utmMedium } = getUTMParams();

  const source = utmMedium
    ? `${utmSource || 'direct'}|${utmMedium}`
    : utmSource || undefined;

  return {
    utm_source: source,
    utm_campaign: utmCampaign || undefined,
    page_url: window.location.href,
  };
}

/** The UTM tags on the current URL, or null when it carries none. */
function utmFromCurrentUrl(): UTMParams | null {
  try {
    const p = new URLSearchParams(window.location.search);
    const get = (k: string) => p.get(k) || '';
    const tags = {
      utmSource: get('utm_source'), utmMedium: get('utm_medium'), utmCampaign: get('utm_campaign'),
      utmContent: get('utm_content'), utmTerm: get('utm_term'),
    };
    return Object.values(tags).some(Boolean) ? tags : null;
  } catch {
    return null;
  }
}

export interface SignupAcquisitionFields {
  utm_source?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  page_url?: string;
}

/** The server caps (enrollmentSchema portalFreeSignupSchema): 255 per UTM field, 500 and http(s) for page_url. */
const UTM_MAX = 255;
const PAGE_URL_MAX = 500;

/**
 * C94: the acquisition keys for POST /api/portal/free-signup, which the signup page never sent, so every ad signup
 * was recorded as a plain `free_signup` with no source. Same `source|medium` convention as getUTMPayloadFields, plus
 * utm_content (which ad) and utm_term (which placement).
 *
 * The route is `.strict()` and validates each value, so this returns ONLY keys that hold a value the server will
 * accept: an empty value is omitted (absent means "not known"), an over-long value is omitted rather than truncated
 * (a cut-off id is a wrong id), and page_url is sent only when it is http(s) and within 500 characters. A signup must
 * never fail because of a tracking value.
 *
 * C104: the tags are read from THIS page's URL first, storage second. Only the public layouts call captureUTMFromURL,
 * and the ad lands on a builder page outside the app, so on /portal/signup the storage is normally empty: measured
 * 0 of 53 ad signups storing utm_content while 443 of 446 ad URLs carried it. A URL with any UTM tag is taken whole
 * (one ad click's tags, never mixed with an older visit's); a bare URL falls back to what storage holds.
 */
export function getSignupAcquisitionFields(): SignupAcquisitionFields {
  const fromUrl = utmFromCurrentUrl();
  const stored = getUTMParams();
  const { utmSource, utmMedium, utmCampaign, utmContent, utmTerm } = fromUrl ?? stored;
  const source = utmMedium ? `${utmSource || 'direct'}|${utmMedium}` : utmSource || undefined;
  const out: SignupAcquisitionFields = {};
  const put = (key: Exclude<keyof SignupAcquisitionFields, 'page_url'>, v: string | undefined) => {
    const s = (v || '').trim();
    if (s && s.length <= UTM_MAX) out[key] = s;
  };
  put('utm_source', source);
  put('utm_campaign', utmCampaign);
  put('utm_content', utmContent);
  put('utm_term', utmTerm);
  try {
    const href = window.location.href;
    if (/^https?:\/\//i.test(href) && href.length <= PAGE_URL_MAX) out.page_url = href;
  } catch { /* no location: leave page_url out */ }
  return out;
}
